import { useEffect, useMemo, useRef } from 'react';
import { useMatches, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { usePreviewMode } from './usePreviewMode';

const getRedirectUrl = (url: string, preview: boolean, isStandalone: boolean): string => {
  let redirectUrl = url;
  const params = new URLSearchParams();

  if (preview) {
    params.set('preview', 'true');
  }
  if (isStandalone) {
    params.set('standalone', 'true');
  }

  const queryString = params.toString();

  if (queryString) {
    redirectUrl = `${redirectUrl}?${queryString}`;
  }

  return redirectUrl;
};

interface RedirectObject {
  [slugMatch: string]: string | undefined;
}

export const useRedirectObject = (redirectObject: RedirectObject): void => {
  const isRedirected = useRef(false);
  const matches = useMatches();
  const location = useLocation();
  const navigate = useNavigate();
  const preview = usePreviewMode();

  const [searchParams] = useSearchParams();
  const isStandalone = searchParams.get('standalone') === 'true';

  const allValuesPresent = useMemo(
    () => Object.keys(redirectObject).length > 0 && Object.values(redirectObject).every(Boolean),
    [redirectObject],
  );

  useEffect(() => {
    if (!allValuesPresent) {
      return;
    }

    const params = matches[matches.length - 1]?.params || {};

    const pathPattern =
      '/' +
      location.pathname
        .split('/')
        .filter(Boolean)
        .map((segment) => {
          const paramKey = Object.keys(params).find((key) => params[key] === segment);

          return paramKey ? `:${paramKey}` : segment;
        })
        .join('/');

    const url = Object.entries(redirectObject).reduce((acc, [key, value]) => acc.replace(key, value!), pathPattern);

    const redirectUrl = getRedirectUrl(url, preview, isStandalone);
    const currentUrl = location.pathname + location.search;

    if (currentUrl !== redirectUrl && !isRedirected.current) {
      isRedirected.current = true;
      navigate(redirectUrl, { replace: true });
    }
  }, [redirectObject, allValuesPresent, matches, location, navigate, preview, isRedirected, isStandalone]);
};
